import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Badge,
  Alert,
  CustomInput,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from 'reactstrap';
import moment from 'moment';
import Header from '../../components/Headers/Header.js';
import { axiosGet, axiosPost } from '../../util/apiConfig';

const REFRESH_MS = 30000;

const statusPill = (session) => {
  if (session.cancelled) {
    return <Badge color="secondary">Cancelled</Badge>;
  }
  if (session.alert) {
    return <Badge color="danger">⚠ Teacher not joined</Badge>;
  }
  if (session.joined) {
    return <Badge color="success">Joined</Badge>;
  }
  return <Badge color="info">Scheduled</Badge>;
};

class SessionMonitor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      loading: true,
      fetching: false,
      errorMsg: '',
      alertsOnly: true,
      lastUpdated: null,
      selectedDate: '', // '' = live rolling window
      expandedSessions: {},
      muteModal: null, // { sectionId, sectionName, mode: 'permanent'|'until', muteUntil, reason, saving, error }
    };
  }

  toggleExpand = (sessionId) => {
    this.setState((prevState) => ({
      expandedSessions: {
        ...prevState.expandedSessions,
        [sessionId]: !prevState.expandedSessions[sessionId],
      },
    }));
  };

  componentDidMount() {
    this.load();
    this.startTimer();
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startTimer = () => {
    this.stopTimer();
    this.timer = setInterval(this.load, REFRESH_MS);
  };

  stopTimer = () => {
    clearInterval(this.timer);
  };

  onDateChange = (e) => {
    const selectedDate = e.target.value;
    this.setState({ selectedDate }, () => {
      if (selectedDate) {
        this.stopTimer();
      } else {
        this.startTimer();
      }
      this.load();
    });
  };

  backToLive = () => {
    this.setState({ selectedDate: '' }, () => {
      this.startTimer();
      this.load();
    });
  };

  openMuteModal = (session) => {
    this.setState({
      muteModal: {
        sectionId: session.section.sectionId,
        sectionName: session.section.name || session.className,
        mode: 'permanent',
        muteUntil: '',
        reason: '',
        saving: false,
        error: '',
      },
    });
  };

  closeMuteModal = () => {
    this.setState({ muteModal: null });
  };

  submitMute = () => {
    const { muteModal } = this.state;
    if (muteModal.mode === 'until' && !muteModal.muteUntil) {
      this.setState({ muteModal: { ...muteModal, error: 'Pick a date to mute until.' } });
      return;
    }
    this.setState({ muteModal: { ...muteModal, saving: true, error: '' } });
    axiosPost('session-monitor/mute', {
      sectionId: muteModal.sectionId,
      muteUntil: muteModal.mode === 'until' ? moment(muteModal.muteUntil).endOf('day').toISOString() : null,
      reason: muteModal.reason,
    })
      .then((res) => {
        if (res && !res.isError) {
          this.closeMuteModal();
          this.load();
        } else {
          this.setState({ muteModal: { ...muteModal, saving: false, error: (res && res.message) || 'Could not mute this section.' } });
        }
      })
      .catch((err) => {
        this.setState({ muteModal: { ...muteModal, saving: false, error: (err && err.message) || 'Could not mute this section.' } });
      });
  };

  unmuteSection = (sectionId) => {
    axiosPost('session-monitor/unmute', { sectionId }).then(() => this.load()).catch(() => this.load());
  };

  load = () => {
    const { selectedDate } = this.state;
    this.setState({ fetching: true });
    const url = selectedDate
      ? `session-monitor?from=${moment(selectedDate).startOf('day').toISOString()}&to=${moment(selectedDate).endOf('day').toISOString()}`
      : 'session-monitor';
    axiosGet(url)
      .then((res) => {
        if (res && !res.isError) {
          this.setState({ data: res.data, loading: false, fetching: false, errorMsg: '', lastUpdated: new Date() });
        } else {
          this.setState({ loading: false, fetching: false, errorMsg: (res && res.message) || 'Could not load session monitor' });
        }
      })
      .catch((err) => {
        this.setState({ loading: false, fetching: false, errorMsg: (err && err.message) || 'Could not load session monitor' });
      });
  };

  render() {
    const { data, loading, fetching, errorMsg, alertsOnly, lastUpdated, selectedDate } = this.state;
    const sessions = data ? (alertsOnly ? data.sessions.filter((s) => s.alert) : data.sessions) : [];

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col md="5">
                  <h3 className="mb-0">Session Monitor</h3>
                  <small className="text-muted">
                    Every regular and demo class in the current window, one screen — red means the teacher hasn&apos;t joined a class that should already be underway.
                  </small>
                </Col>
                <Col className="text-right" md="7">
                  <CustomInput
                    type="checkbox"
                    id="alertsOnlyToggle"
                    label="Show alerts only"
                    checked={alertsOnly}
                    onChange={(e) => this.setState({ alertsOnly: e.target.checked })}
                    className="d-inline-block mr-3"
                    style={{ verticalAlign: 'middle' }}
                  />
                  <Input
                    type="date"
                    bsSize="sm"
                    value={selectedDate}
                    onChange={this.onDateChange}
                    className="d-inline-block mr-2"
                    style={{ width: '160px', verticalAlign: 'middle' }}
                  />
                  {selectedDate ? (
                    <Button size="sm" color="primary" outline onClick={this.backToLive} className="mr-2">
                      Back to live
                    </Button>
                  ) : null}
                  <Button size="sm" color="secondary" outline onClick={this.load} disabled={fetching}>
                    {fetching ? 'Refreshing…' : 'Refresh now'}
                  </Button>
                </Col>
              </Row>
              {data ? (
                <Row className="mt-3">
                  <Col>
                    <span className="mr-3"><strong>{data.totalCount}</strong> sessions in window</span>
                    <span className={data.alertCount > 0 ? 'text-danger font-weight-bold mr-3' : 'text-muted mr-3'}>
                      {data.alertCount > 0 ? `⚠ ${data.alertCount} need attention` : 'No alerts'}
                    </span>
                    <span className="text-muted mr-3">
                      {moment(data.from).format('MMM D, h:mm A')} – {moment(data.to).format('MMM D, h:mm A')}
                    </span>
                    {lastUpdated ? (
                      <span className="text-muted">
                        {selectedDate ? 'Historic view · not auto-refreshing' : 'Auto-refreshes every 30s'} · last updated {moment(lastUpdated).format('h:mm:ss A')}
                      </span>
                    ) : null}
                  </Col>
                </Row>
              ) : null}
              {errorMsg ? <Alert color="danger" className="mt-3 mb-0 py-2">{errorMsg}</Alert> : null}
            </CardHeader>

            <CardBody className="p-0">
              <div style={{ overflowX: 'auto' }}>
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Time</th>
                      <th scope="col">Type</th>
                      <th scope="col">Class / Student</th>
                      <th scope="col">Teacher</th>
                      <th scope="col">Status</th>
                      <th scope="col">Zoom</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="7" className="text-center py-4"><Spinner /></td></tr>
                    ) : sessions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          {alertsOnly ? 'No sessions need attention right now.' : 'No sessions in this window.'}
                        </td>
                      </tr>
                    ) : (
                      sessions.map((s) => {
                        const isExpanded = !!this.state.expandedSessions[s.id];
                        return (
                          <React.Fragment key={s.id}>
                            <tr style={s.alert ? { backgroundColor: '#fdecea' } : undefined}>
                              <td>
                                <span className="font-weight-bold">{moment(s.time).format('MMM D')}</span>
                                <br />
                                <small className="text-muted">{moment(s.time).format('h:mm A')}</small>
                              </td>
                              <td>
                                <Badge color={s.type === 'demo' ? 'primary' : 'secondary'}>
                                  {s.type === 'demo' ? 'Demo' : 'Regular'}
                                </Badge>
                              </td>
                              <td>
                                {s.type === 'regular' && s.section ? (
                                  <span
                                    className="mr-2 cursor-pointer text-primary"
                                    onClick={() => this.toggleExpand(s.id)}
                                    style={{ display: 'inline-block', width: '12px', textAlign: 'center' }}
                                  >
                                    <i className={`fas fa-caret-${isExpanded ? 'down' : 'right'}`} />
                                  </span>
                                ) : s.type === 'regular' ? (
                                  <span style={{ display: 'inline-block', width: '12px' }} className="mr-2" />
                                ) : null}
                                <span className={s.type === 'regular' && s.section ? 'cursor-pointer font-weight-bold' : ''} onClick={s.type === 'regular' && s.section ? () => this.toggleExpand(s.id) : undefined}>
                                  {s.className}
                                </span>
                                {s.type === 'regular' && s.section ? (
                                  <>
                                    <br />
                                    <small className="text-muted">
                                      Section: {s.section.name || '—'} · {s.section.curriculumName || '—'}
                                    </small>
                                  </>
                                ) : null}
                                {s.studentName ? <><br /><small className="text-muted">{s.studentName}</small></> : null}
                              </td>
                              <td>{s.teacherName}</td>
                              <td>
                                {statusPill(s)}
                                <br />
                                <small className="text-muted">{s.status}</small>
                                {s.joinedAt ? (
                                  <>
                                    <br />
                                    <small className="text-muted">Joined {moment(s.joinedAt).format('h:mm A')}</small>
                                  </>
                                ) : null}
                              </td>
                              <td>
                                {s.zoomLink ? (
                                  <a href={s.zoomLink} target="_blank" rel="noopener noreferrer">Join</a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {s.type === 'regular' && s.section ? (
                                  s.muted ? (
                                    <>
                                      <Badge color="warning" className="mb-1 d-block">
                                        Muted{s.muteUntil ? ` until ${moment(s.muteUntil).format('MMM D')}` : ' (permanent)'}
                                      </Badge>
                                      <Button size="sm" color="link" className="p-0" onClick={() => this.unmuteSection(s.section.sectionId)}>
                                        Unmute
                                      </Button>
                                    </>
                                  ) : (
                                    <Button size="sm" color="link" className="p-0" onClick={() => this.openMuteModal(s)}>
                                      Mute alert
                                    </Button>
                                  )
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && s.section && (
                              <tr style={s.alert ? { backgroundColor: '#fdf3f2' } : { backgroundColor: '#fafbfc' }}>
                                <td colSpan="7" className="p-3">
                                  <div className="pl-4">
                                    <Row>
                                      <Col md="4" sm="6" className="mb-2">
                                        <strong>Curriculum:</strong> {s.section.curriculumName || '—'}
                                      </Col>
                                      <Col md="4" sm="6" className="mb-2">
                                        <strong>Schedule:</strong> {s.section.daysOfWeek ? s.section.daysOfWeek.filter(d => d.isActive).map(d => d.name || d.key || d).join(', ') : '—'} ({s.section.startTime || '—'} - {s.section.endTime || '—'} {s.section.localTimeZone || ''})
                                      </Col>
                                      <Col md="4" sm="6" className="mb-2">
                                        <strong>Class Size:</strong> {s.section.classSize || '—'}
                                      </Col>
                                      <Col md="4" sm="6" className="mb-2">
                                        <strong>Type:</strong> {s.section.sectionType === 2 ? 'Private' : 'Public'}
                                      </Col>
                                      {s.section.classId === 'CLASS1648760926141' && (
                                        // Belt (beginner/intermediate/advanced) is a Chess-only concept.
                                        <Col md="4" sm="6" className="mb-2">
                                          <strong>Belt:</strong> {s.section.belt || '—'}
                                        </Col>
                                      )}
                                    </Row>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>

        {this.state.muteModal ? (
          <Modal isOpen toggle={this.closeMuteModal}>
            <ModalHeader toggle={this.closeMuteModal}>Mute alert — {this.state.muteModal.sectionName}</ModalHeader>
            <ModalBody>
              {this.state.muteModal.error ? <Alert color="danger" className="py-2">{this.state.muteModal.error}</Alert> : null}
              <FormGroup tag="fieldset">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="radio"
                      name="muteMode"
                      checked={this.state.muteModal.mode === 'permanent'}
                      onChange={() => this.setState({ muteModal: { ...this.state.muteModal, mode: 'permanent' } })}
                    />{' '}
                    Permanent — stays muted until manually unmuted
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="radio"
                      name="muteMode"
                      checked={this.state.muteModal.mode === 'until'}
                      onChange={() => this.setState({ muteModal: { ...this.state.muteModal, mode: 'until' } })}
                    />{' '}
                    Until a specific date
                  </Label>
                </FormGroup>
              </FormGroup>
              {this.state.muteModal.mode === 'until' ? (
                <FormGroup>
                  <Label>Mute until</Label>
                  <Input
                    type="date"
                    value={this.state.muteModal.muteUntil}
                    onChange={(e) => this.setState({ muteModal: { ...this.state.muteModal, muteUntil: e.target.value } })}
                  />
                </FormGroup>
              ) : null}
              <FormGroup>
                <Label>Reason (optional)</Label>
                <Input
                  type="textarea"
                  value={this.state.muteModal.reason}
                  onChange={(e) => this.setState({ muteModal: { ...this.state.muteModal, reason: e.target.value } })}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={this.closeMuteModal}>Cancel</Button>
              <Button color="warning" onClick={this.submitMute} disabled={this.state.muteModal.saving}>
                {this.state.muteModal.saving ? 'Muting…' : 'Mute'}
              </Button>
            </ModalFooter>
          </Modal>
        ) : null}
      </>
    );
  }
}

export default SessionMonitor;
